import { Field } from '@today/ui-components'
import { Button, Spinner } from '@today/ui-primitives'
import { useState } from 'react'
import { type AuthLogo, AuthShellView } from '../auth-shell/auth-shell.view'

/** Which step of the invitation first login the page is on (TO-309). */
export enum InvitationFirstLoginStage {
	/** The link is being checked. */
	LOADING = 'LOADING',
	/** A native tenant: the user chooses their password. */
	SET_PASSWORD = 'SET_PASSWORD',
	/** Leaving for the hosted sign-in page (or the post-login destination). */
	REDIRECTING = 'REDIRECTING',
	/** The link cannot be used; `errorCode` says why. */
	ERROR = 'ERROR',
}

/**
 * The copy of {@link InvitationFirstLoginView} the federated return leg shows
 * (TO-728). Interpolated labels are functions; `refusals` is keyed by the API's
 * refusal code. The password form's copy is not here yet (TO-727).
 */
export interface InvitationFirstLoginLabels {
	title: (slug: string) => string
	checking: string
	redirecting: string
	invalidLink: string
	refusals: Partial<Record<string, string>>
}

/**
 * Props for {@link InvitationFirstLoginView}.
 *
 * @property tenantSlug - The tenant the invitation belongs to, from the URL.
 * @property logo - The bound brand's logo for the auth frame; omitted, no image renders.
 * @property stage - The current step.
 * @property email - The invited address, shown on the set-password step.
 * @property errorCode - The API's machine-readable refusal code on the error step.
 * @property passwordRejected - True when the pool's password policy refused the last submission.
 * @property isSubmitting - True while the chosen password is being set.
 * @property onSubmitPassword - Sets the chosen password (called only when both fields match).
 * @property labels - The copy; any omitted label (or refusal code) falls back to English.
 */
export interface InvitationFirstLoginViewProps {
	tenantSlug: string
	logo?: AuthLogo
	stage: InvitationFirstLoginStage
	email?: string
	errorCode?: string | null
	passwordRejected?: boolean
	isSubmitting?: boolean
	onSubmitPassword: (password: string) => void
	labels?: Partial<InvitationFirstLoginLabels>
}

const DEFAULT_LABELS: InvitationFirstLoginLabels = {
	title: (slug) => `Join ${slug}`,
	checking: 'Checking your invitation…',
	redirecting: 'Taking you to sign in…',
	invalidLink: 'This invitation link is not valid.',
	refusals: {
		INVITATION_EXPIRED: 'This invitation has expired. Ask your administrator to send a new one.',
		INVITATION_REVOKED: 'This invitation was cancelled by your administrator.',
		INVITATION_SUPERSEDED: 'A newer invitation was sent to you. Use the link in the most recent email.',
		INVITATION_ALREADY_USED: 'This invitation has already been used. Sign in instead.',
		FEDERATED_IDENTITY_CONFLICT:
			"Your organisation account is already in use and can't be linked. Contact your administrator.",
		FEDERATED_LOGIN_REQUIRED: 'Sign in through your organisation to finish accepting this invitation.',
	},
}

/** The supplied copy over the English defaults; refusals merge per code, so an omitted code keeps English. */
const resolveLabels = (labels: Partial<InvitationFirstLoginLabels> = {}): InvitationFirstLoginLabels => ({
	...DEFAULT_LABELS,
	...labels,
	refusals: { ...DEFAULT_LABELS.refusals, ...labels.refusals },
})

/** The two password fields, and the problem to show (mismatch first, then a policy refusal). */
function usePasswordPair(passwordRejected: boolean | undefined, onSubmitPassword: (password: string) => void) {
	const [password, setPassword] = useState('')
	const [confirmation, setConfirmation] = useState('')
	const [mismatch, setMismatch] = useState(false)

	const submit = () => {
		const matches = password === confirmation
		setMismatch(!matches)
		if (matches) onSubmitPassword(password)
	}
	const problem = mismatch
		? 'The passwords do not match.'
		: passwordRejected
			? "That password doesn't meet the password policy. Choose a longer or more varied one."
			: null
	return { password, setPassword, confirmation, setConfirmation, submit, problem }
}

function SetPasswordForm({
	email,
	passwordRejected,
	isSubmitting,
	onSubmitPassword,
}: Pick<InvitationFirstLoginViewProps, 'email' | 'passwordRejected' | 'isSubmitting' | 'onSubmitPassword'>) {
	const { password, setPassword, confirmation, setConfirmation, submit, problem } = usePasswordPair(
		passwordRejected,
		onSubmitPassword,
	)

	return (
		<form
			className="flex w-full flex-col gap-4"
			onSubmit={(event) => {
				event.preventDefault()
				submit()
			}}
		>
			{email ? <p className="text-ds-sm text-ds-text-muted">Choose a password for {email}.</p> : null}
			<Field
				label="New password"
				type="password"
				autoComplete="new-password"
				minLength={8}
				value={password}
				onChange={(event) => setPassword(event.target.value)}
			/>
			<Field
				label="Confirm password"
				type="password"
				autoComplete="new-password"
				value={confirmation}
				onChange={(event) => setConfirmation(event.target.value)}
			/>
			{problem ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{problem}
				</p>
			) : null}
			<Button type="submit" variant="primary" isDisabled={isSubmitting}>
				Set password and continue
			</Button>
		</form>
	)
}

/**
 * The invitation first-login surface (TO-309, REQ-002). Pure: the page's hook
 * checks the link, sets the password and starts sign-in; this view renders the
 * current step. The password never leaves `onSubmitPassword`.
 *
 * Composes the shared {@link AuthShellView} frame (TO-637) so the public first
 * leg and the federated return leg render the same card; the frame owns the
 * screen's single h1.
 */
export function InvitationFirstLoginView(props: InvitationFirstLoginViewProps) {
	const { tenantSlug, logo, stage, errorCode, labels } = props
	const copy = resolveLabels(labels)
	return (
		<AuthShellView logo={logo} title={copy.title(tenantSlug)}>
			{stage === InvitationFirstLoginStage.SET_PASSWORD ? <SetPasswordForm {...props} /> : null}
			{stage === InvitationFirstLoginStage.ERROR ? (
				<p role="alert" className="text-center text-ds-sm text-ds-danger">
					{(errorCode && copy.refusals[errorCode]) || copy.invalidLink}
				</p>
			) : null}
			{stage === InvitationFirstLoginStage.LOADING || stage === InvitationFirstLoginStage.REDIRECTING ? (
				<p className="flex items-center gap-2 text-ds-sm text-ds-text-muted" aria-live="polite">
					<Spinner size="sm" />
					{stage === InvitationFirstLoginStage.LOADING ? copy.checking : copy.redirecting}
				</p>
			) : null}
		</AuthShellView>
	)
}
